import type {
	JourneyDefinition,
	PaymentTiming,
	QuestionnaireTemplate,
} from "../contracts"
import {
	createPatientOrderMachine,
	type PatientOrderMachine,
} from "../machine/patient-order-machine"
import {
	DEFAULT_T_SPRAY_JOURNEY_DEFINITION,
	DEFERRED_LABS_T_SPRAY_JOURNEY_DEFINITION,
	JOURNEY_DEFINITIONS,
	findJourneyDefinition,
	getDefaultJourneyDefinitionForProductKey,
	getJourneyDefinition,
	getJourneyDefinitionForTemplateReferences,
	getJourneyDefinitionsForProductKey,
} from "../templates/journey-definitions"
import { getQuestionnaireTemplate } from "../templates/questionnaire-templates"
import {
	getWorkflowTemplate,
	type WorkflowTemplate,
} from "../templates/workflow-templates"

export type PatientJourneyRuntime = {
	readonly definition: JourneyDefinition
	readonly workflowTemplate: WorkflowTemplate
	readonly questionnaireTemplate: QuestionnaireTemplate
	readonly machine: PatientOrderMachine
}

const runtimeByJourneyVersion = new Map<string, PatientJourneyRuntime>()

export function getPatientJourneyRuntime(
	journeyDefinitionId: string,
	journeyDefinitionVersion?: number,
): PatientJourneyRuntime {
	const definition = getJourneyDefinition(journeyDefinitionId, journeyDefinitionVersion)
	const key = `${definition.id}@${definition.version}`
	const cached = runtimeByJourneyVersion.get(key)
	if (cached !== undefined) {
		return cached
	}

	const workflowTemplate = getWorkflowTemplate(
		definition.workflow.id,
		definition.workflow.version,
	)
	const questionnaireTemplate = getQuestionnaireTemplate(
		definition.questionnaire.id,
		definition.questionnaire.version,
	)
	const runtime = {
		definition,
		workflowTemplate,
		questionnaireTemplate,
		machine: createPatientOrderMachine({ workflowTemplate, questionnaireTemplate }),
	} satisfies PatientJourneyRuntime
	runtimeByJourneyVersion.set(key, runtime)
	return runtime
}

export function getPatientJourneyRuntimeForTemplateReferences(args: {
	readonly journeyDefinitionId: string
	readonly workflowTemplateId: string
	readonly workflowTemplateVersion: number
	readonly questionnaireTemplateId: string
	readonly questionnaireTemplateVersion: string
}): PatientJourneyRuntime {
	const definition = getJourneyDefinitionForTemplateReferences(args)
	return getPatientJourneyRuntime(definition.id, definition.version)
}

export function getPatientJourneyPaymentTiming(args: {
	readonly journeyDefinitionId: string
	readonly journeyDefinitionVersion?: number
	readonly labFulfillmentMethod: "ship_kit" | "upload_existing_results"
}): PaymentTiming {
	const runtime = getPatientJourneyRuntime(
		args.journeyDefinitionId,
		args.journeyDefinitionVersion,
	)
	return args.labFulfillmentMethod === "ship_kit"
		? runtime.workflowTemplate.policies.shipKitPaymentTiming
		: runtime.workflowTemplate.policies.uploadedLabsPaymentTiming
}

export const patientJourneys = {
	definitions: JOURNEY_DEFINITIONS,
	defaultTSprayDefinition: DEFAULT_T_SPRAY_JOURNEY_DEFINITION,
	deferredLabsTSprayDefinition: DEFERRED_LABS_T_SPRAY_JOURNEY_DEFINITION,
	findDefinition: findJourneyDefinition,
	definition: getJourneyDefinition,
	definitionForTemplateReferences: getJourneyDefinitionForTemplateReferences,
	definitionsForProduct: getJourneyDefinitionsForProductKey,
	defaultDefinitionForProduct: getDefaultJourneyDefinitionForProductKey,
	runtime: getPatientJourneyRuntime,
	runtimeForTemplateReferences: getPatientJourneyRuntimeForTemplateReferences,
	paymentTiming: getPatientJourneyPaymentTiming,
} as const
